import React, { useMemo, useEffect, useCallback } from "react";
import useSWR from "swr";
import { jwtDecode } from "jwt-decode";
import addDays from "date-fns/addDays";
import useSWRMutation from "swr/mutation";

import { ASSISTANT_ROLE, SELLER_ROLE } from "../../helpers/role";
import { useGetToken } from "./useGetToken";
import { fetchHandleError, sendRequest } from "../../helpers/fetchHandleError";

export const useUserMe = () => {
  return useSWR(`${process.env.REACT_APP_CMS_HOST}/users/me`, {
    revalidateOnFocus: false,
    refreshInterval: 60 * 60 * 1000,
  });
};

export const useUserRole = () => {
  const { data } = useUserMe();
  if (!data) {
    return ASSISTANT_ROLE;
  }
  return data.role.name;
};
export const useIsSeller = () => {
  const role = useUserRole();
  if (role === SELLER_ROLE) {
    return true;
  }
  return false;
};
export const withSellerAdminShow = (WrappedComponent) => (props) => {
  const isSeller = useIsSeller();
  if (isSeller) {
    return <WrappedComponent {...props} />;
  }
  return null;
};

export const useGetSellers = () => {
  return useSWR(`${process.env.REACT_APP_CMS_HOST}/pages/sellers`, {
    revalidateOnFocus: false,
  });
};

export const useUpdateAssistantPassword = (userId, option) => {
  return useSWRMutation(
    userId
      ? `${process.env.REACT_APP_CMS_HOST}/pages/assistant/${userId}/change-password`
      : null,
    (url, config) => sendRequest(url, { ...config, method: "PUT" }),
    {
      ...option,
    }
  );
};

export const useUpdateSellerPassword = (
  url = `${process.env.REACT_APP_CMS_HOST}/pages/seller/change-password`,
  option
) =>
  useSWRMutation(
    url,
    (key, config) => sendRequest(key, { ...config, method: "PUT" }),
    { ...option }
  );

export const useGetAssistants = () => {
  return useSWR(`${process.env.REACT_APP_CMS_HOST}/pages/assistants`, {
    revalidateOnFocus: false,
  });
};

export const useUserExtension = () => {
  const token = useGetToken();

  const { data: userMe, mutate: userMeMutate } = useUserMe();
  const userExtension = useSWR(
    userMe && userMe.userextension
      ? `${process.env.REACT_APP_CMS_HOST}/userextensions/${userMe.userextension}`
      : null,
    {
      revalidateOnFocus: false,
      refreshInterval: 15 * 60 * 1000,
    }
  );

  const checkAndCreate = useCallback(() => {
    if (userMe && !userMe.userextension && !userExtension.data) {
      fetch(`${process.env.REACT_APP_CMS_HOST}/userextensions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({}),
      })
        .then(fetchHandleError)
        .then((r) => userMeMutate())
        .catch((e) => {});
    }
  }, [token, userExtension.data, userMe, userMeMutate]);

  useEffect(() => {
    if (userMe && !userMe.userextension && !userExtension.data) {
      const timer = setTimeout(checkAndCreate, 20000);
      return () => clearTimeout(timer);
    }
  }, [checkAndCreate, userExtension.data, userMe]);

  return userExtension;
};

export const useShouldLogout = () => {
  const token = useGetToken();

  const { data: userExtension } = useUserExtension();
  const shouldLogout = useMemo(() => {
    if (token && userExtension && userExtension.lastPasswordChange) {
      const decoded = jwtDecode(token);
      const tokenExpiryDate = new Date(decoded.exp * 1000);
      const changeDatePlus7 = addDays(
        new Date(userExtension.lastPasswordChange),
        7
      );
      if (changeDatePlus7 >= tokenExpiryDate) {
        return true;
      }
    }
    return false;
  }, [token, userExtension]);

  return shouldLogout;
};
